import React, { useState, useEffect, useRef, FormEvent, useContext } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import '../../App.css';
import SearchResultsModal from '../layout/SearchResultsModal';
import SearchService from '../../services/SearchService';
import { AxiosError, AxiosResponse } from 'axios';

import { searchRequest, searchResults, documentDefinition } from '../../types/Search';
import ActivityHistory from '../layout/ActivityHistory';
import '../../assets/css/SearchBar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMagnifyingGlass,
    faFileArrowDown,
    faClockRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

import StatePicker from './search/StatePicker';
import CountyPicker from './search/CountyPicker';
import FormatPicker from './search/FormatPicker';
import SearchByPicker from './search/SearchByPicker';
import VariableSearchFields from './search/VariableSearchFields';
import ImagesList from './search/ImagesList';

import LoadingContext from './search/loading-context';
import SearchDataContext from '../../providers/searchData-context';
import ImageDataContext from '../../providers/imageData-context';

import originLogoAndText from '../../assets/img/origin-icon-and-text.svg';
import { Accordion, Button, Card, Form } from 'react-bootstrap';
import FolderViewModal from './FolderViewModal';

export interface Props {}

export const SearchBar: React.FC<Props> = (props) => {
    const { oktaAuth, authState } = useOktaAuth();
    const loadingCtx = useContext(LoadingContext);
    const searchDataCtx = useContext(SearchDataContext);
    const imageDataCtx = useContext(ImageDataContext);

    const [validationshow, setValidationShow] = useState(true);
    const [validated, setValidated] = useState(false);
    const [error, setError] = useState<AxiosError>();
    const [isError, setIsError] = useState(false);
    const [response, setResponse] = useState<AxiosResponse>();

    const [show, setShow] = useState(false);
    const handleShow = () => setShow(true);

    const [showFolderView, setShowFolderView] = useState(false);

    const accordionRef = useRef<HTMLDivElement>(null);
    const [resultModal, setResultModal] = useState<searchResults>();

    const [refreshHistoryTickler, setRefreshHistoryTickler] = useState<number>(0);

    const searchimagereq: searchRequest = {
        countyId: searchDataCtx.countyId,
        format: searchDataCtx.format,
        searchBy: searchDataCtx.searchBy,
        isReverse: searchDataCtx.isReverse,
        pageIndex: 0,
        pageSize: 250,
    };

    const [searchExpanded, setSearchExpanded] = useState<string>('0');
    const [imageListExpanded, setImageListExpanded] = useState<string>('');
    const [activityListExpanded, setActivityListExpanded] = useState<string>('');

    const [messageForSearchResults, setMessageForSearchResults] = useState('');

    var uid: string = '';
    var userName: string = '';
    var accessToken: string = '';
    var groupName: string = '';

    if (!authState) {
        return <div></div>;
    }

    if (authState.isAuthenticated) {
        userName = authState.accessToken.claims?.sub;
        uid = authState.accessToken.claims?.uid;
        accessToken = authState.accessToken.accessToken;
    }

    function handleClose() {
        setShow(false);
    }

    function handleCloseWithRefresh() {
        setShow(false);
        setImageListExpanded('0');
    }

    const displayLabel = searchDataCtx.searchByDescription ?? '';
    const displayValue = searchDataCtx.fields?.map((f) => f.name).join('/') ?? '';

    const formatDate = (dateValue: string) => {
        return moment(dateValue).format('YYYY-MM-DD');
    };

    const coverageLabel =
        searchDataCtx.coverageStart && searchDataCtx.coverageEnd
            ? 'Coverage From: ' +
              formatDate(searchDataCtx.coverageStart!) +
              ' - ' +
              formatDate(searchDataCtx.coverageEnd!)
            : '';

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(undefined);
        setIsError(false);
        setResponse(undefined);

        const form = e.currentTarget;

        setValidated(true);
        setValidationShow(true);
        setShow(false);

        setResultModal(undefined);

        if (
            searchDataCtx.state &&
            searchDataCtx.countyId &&
            searchDataCtx.format &&
            searchDataCtx.searchBy &&
            searchDataCtx.fields
        ) {
            const fieldsWithAnyValues = searchDataCtx.fields?.filter((field) => {
                return field.value && field.value.length > 0 && field.value !== '*';
            });
            if (fieldsWithAnyValues.length === 0) {
                searchDataCtx.setSearchMessage(
                    'Insufficient search parameters. Please enter additional search criteria'
                );
                return;
            }

            getSearchImages(searchimagereq);
            setValidationShow(false);
            setValidated(false);
            searchDataCtx.setSearchMessage('');
        } else {
            searchDataCtx.setSearchMessage('');
        }
    };

    const getSearchImages = (imgRequest: searchRequest) => {
        setMessageForSearchResults('');
        loadingCtx.setIsLoading(true, 'SearchBar_LoadingSearch');
        if (!searchDataCtx.fields) return;

        SearchService.getSearchImages(imgRequest, accessToken, uid, searchDataCtx.fields)
            .then((response) => {
                let searchResultsData = response.data as searchResults;
                if (searchResultsData.results.length === 1) {
                    setShow(false);
                    var singleData: Array<documentDefinition> = [];
                    searchResultsData.results?.forEach((d: documentDefinition) => {
                        singleData.push(d);
                    });
                    imageDataCtx.addImagesToImagesList(singleData);

                    if (singleData.length > 0) {
                        setImageListExpanded('0');
                    }
                } else if (searchResultsData.results.length === 0) {
                    setShow(false);
                    searchDataCtx.setSearchMessage('No images matching your search criteria');
                } else if (searchResultsData.results.length === 1001) {
                    setShow(true);
                    setMessageForSearchResults(
                        'There are too many results matching your search criteria, please refine your search and try again'
                    );
                } else {
                    setShow(true);
                }
                if (searchResultsData.results.length === 1001) {
                    searchResultsData.results.pop();
                }
                setResultModal(searchResultsData);

                loadingCtx.setIsLoading(false, 'SearchBar_LoadingSearch');
            })
            .catch((ex) => {
                setError(ex.response.data);
                searchDataCtx.setSearchMessage('An error occurred during the search');
                setIsError(true);
                loadingCtx.setIsLoading(false, 'SearchBar_LoadingSearch');
            });
    };

    const refreshActivityHistory = () => {
        setRefreshHistoryTickler(refreshHistoryTickler + 1);
    };

    let searchDataProvided = false;
    if (
        searchDataCtx.state &&
        searchDataCtx.countyId &&
        searchDataCtx.format &&
        searchDataCtx.searchBy &&
        searchDataCtx.fields
    ) {
        const fieldsWithAnyValues = searchDataCtx.fields?.filter((field) => {
            return field.value && field.value.length > 0 && field.value !== '*';
        });
        if (fieldsWithAnyValues.length > 0) {
            searchDataProvided = true;
        }
    }

    const folderViewCloseHandler = () => {
        setShowFolderView(false);
    };

    const folderViewCloseWithRefreshHandler = () => {
        setShowFolderView(false);
        setImageListExpanded('0');
    };

    return (
        <>
            {resultModal && (
                <SearchResultsModal
                    refreshHistoryCallback={refreshActivityHistory}
                    key='Image-vault-modal'
                    displayInfo={displayLabel}
                    displayInfoNames={displayValue}
                    searchResultsData={resultModal}
                    searchimageReq={searchimagereq}
                    show={show}
                    handleClose={handleClose}
                    handleCloseWithRefresh={handleCloseWithRefresh}
                    handleShow={handleShow}
                    selectedCountyName={searchDataCtx.countyName}
                    messageForSearchResults={messageForSearchResults}
                ></SearchResultsModal>
            )}
            {showFolderView && (
                <FolderViewModal
                    handleClose={folderViewCloseHandler}
                    handleCloseWithRefresh={folderViewCloseWithRefreshHandler}
                    fips={searchDataCtx.countyId}
                    state={searchDataCtx.state}
                    county={searchDataCtx.countyName}
                />
            )}
            <div className='sidebar-header'>
                <div className='sidebar-logo'>
                    <div>
                        <img src={originLogoAndText} alt='company logo' height='70px'></img>
                    </div>
                </div>
            </div>
            <div className='sidebar-body'>
                <div className='flex-container'>
                    <div ref={accordionRef} className='global-page-height side-panel-width'>
                        <Accordion
                            activeKey={searchExpanded}
                            onSelect={(eventKey) => {
                                setSearchExpanded(eventKey?.toString() ?? '');
                            }}
                        >
                            <Accordion.Item eventKey='0'>
                                <Accordion.Header>
                                    <div className='col accordian-icons'>
                                        <FontAwesomeIcon
                                            icon={faMagnifyingGlass}
                                            className='menuIcon'
                                        />
                                        <label className='accordian-labels'>Search</label>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <Card.Body>
                                        <Form
                                            onSubmit={handleSubmit}
                                            noValidate
                                            validated={validated}
                                            data-se='o-form'
                                            slot='content'
                                            id='formSearch'
                                        >
                                            {/* new row -1 */}
                                            <div className='row sidebar-texts row-pad'>
                                                <div className='col'>
                                                    <StatePicker />
                                                </div>
                                                <div className='col'>
                                                    <CountyPicker />
                                                </div>
                                            </div>

                                            {/* row -2 */}
                                            <div className='row sidebar-texts row-pad'>
                                                {searchDataCtx.state &&
                                                    searchDataCtx.countyId &&
                                                    !searchDataCtx.isReverse &&
                                                    (searchDataCtx.countySupportedPlatform ===
                                                        'Both' ||
                                                        searchDataCtx.countySupportedPlatform ===
                                                            'FNIVSearch') && (
                                                        <>
                                                            <div className='col'>
                                                                <FormatPicker />
                                                            </div>
                                                            <div className='col'>
                                                                <SearchByPicker />
                                                            </div>
                                                        </>
                                                    )}
                                                {searchDataCtx.state &&
                                                    searchDataCtx.countyId &&
                                                    searchDataCtx.isReverse &&
                                                    (searchDataCtx.countySupportedPlatform ===
                                                        'Both' ||
                                                        searchDataCtx.countySupportedPlatform ===
                                                            'FNIVSearch') && (
                                                        <>
                                                            <div className='col'>
                                                                <SearchByPicker />
                                                            </div>
                                                            <div className='col'>
                                                                <FormatPicker />
                                                            </div>
                                                        </>
                                                    )}
                                            </div>

                                            {searchDataCtx.searchBy && (
                                                <>
                                                    {/* row- 3 */}
                                                    <div className='row'>
                                                        <div className='form-group'>
                                                            {coverageLabel != '' && (
                                                                <label className='fw-100'>
                                                                    {' '}
                                                                    {coverageLabel}{' '}
                                                                </label>
                                                            )}
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {searchDataCtx.fields &&
                                                searchDataCtx.fields.length > 0 && (
                                                    <VariableSearchFields />
                                                )}

                                            <div className='row sidebar-texts row-pad error-width'>
                                                {searchDataCtx.searchMessage !== '' && (
                                                    <span
                                                        style={{
                                                            color: 'red',
                                                            fontSize: '14px',
                                                        }}
                                                    >
                                                        {searchDataCtx.searchMessage}
                                                    </span>
                                                )}
                                            </div>
                                            {/* row- 5 */}
                                            <div className='row sidebar-texts'>
                                                <div className='form-group search-btn-container'>
                                                    {(searchDataCtx.countySupportedPlatform ===
                                                        'Both' ||
                                                        searchDataCtx.countySupportedPlatform ===
                                                            'Hierarchy') && (
                                                        <button
                                                            type='button'
                                                            onClick={() => {
                                                                setShowFolderView(true);
                                                            }}
                                                            tabIndex={10}
                                                            className='btn btn-primary btn-sm search-btn'
                                                        >
                                                            Open Folder View
                                                        </button>
                                                    )}
                                                    {(searchDataCtx.countySupportedPlatform ===
                                                        'Both' ||
                                                        searchDataCtx.countySupportedPlatform ===
                                                            'FNIVSearch') && (
                                                        <button
                                                            type='submit'
                                                            tabIndex={10}
                                                            onClick={handleShow}
                                                            style={{ marginLeft: '.5rem' }}
                                                            className={
                                                                searchDataProvided
                                                                    ? 'btn btn-primary btn-sm search-btn'
                                                                    : 'btn btn-primary btn-sm search-btn disabled'
                                                            }
                                                        >
                                                            Search
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                        <Accordion
                            activeKey={imageListExpanded}
                            onSelect={(eventKey) => {
                                setImageListExpanded(eventKey?.toString() ?? '');
                            }}
                        >
                            <Accordion.Item eventKey='0'>
                                <Accordion.Header>
                                    <div className='col accordian-icons'>
                                        <FontAwesomeIcon
                                            icon={faFileArrowDown}
                                            className='menuIcon'
                                        />
                                        <label className='accordian-labels'>Saved Images</label>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <Card.Body>
                                        <ImagesList key='key-image-vault-picklist' />
                                    </Card.Body>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                        <Accordion
                            activeKey={activityListExpanded}
                            onSelect={(eventKey) => {
                                setActivityListExpanded(eventKey?.toString() ?? '');
                            }}
                        >
                            <Accordion.Item eventKey='0'>
                                <Accordion.Header>
                                    <div className='col accordian-icons'>
                                        <FontAwesomeIcon
                                            icon={faClockRotateLeft}
                                            className='menuIcon'
                                        />
                                        <label className='accordian-labels'>Activity History</label>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <Card.Body>
                                        <ActivityHistory />
                                    </Card.Body>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SearchBar;
